import { Select } from "./ui/select.tsx";
import { notify } from "./feedback.ts";
import { useMutation, useQuery } from "@tanstack/react-query";
import type * as C from "@zooi/contracts";
import { type FormEvent, useId, useState } from "react";
import { api, q } from "./api.ts";
import { useRefresh } from "./components.tsx";
import { localToday } from "./format.ts";
import { LEARNING_STATUS, lessonText, MEASUREMENT_LABELS as L, METRIC_LABELS, reasonText } from "./labels.ts";
import { Button, ErrorText, Field, Pill, Section } from "./ui.tsx";

// The measurement plan (docs/design-system.md §5.11, design/plan.md §5.3 R2a
// item 14): written before the work, fixed when it is delivered. The server
// decides units and validates every revision; the form only collects input.

type Plan = C.MeasurementPlan;
type Unit = C.Expectation["unit"];

/** The unit an expectation would be written in for a mode the form has not saved yet. */
function unitIn(mode: Plan["mode"], action: C.ActionDetail, metricKind: string | null): Unit | null {
  if (mode === "operational") return null;
  if (mode === "experiment") return metricKind ? (metricKind === "rate" ? "points" : "percent") : null;
  if (mode === action.measurement_plan.mode) return action.expectation_unit;
  return action.measurement_plan.attribution_unit ? "count" : null;
}

const refKey = (r: C.EvidenceRef) => `${r.kind}:${r.id}`;

function PlanProperties({ plan }: { plan: Plan }) {
  const x = plan.experiment;
  return (
    <dl className="z-props">
      <dt>{L.activation}</dt>
      <dd>{L.activations[plan.activation] ?? plan.activation}</dd>
      <dt>{L.window}</dt>
      <dd>{L.windowText(plan.window)}</dd>
      {plan.mode !== "operational" && (
        <>
          <dt>{L.signals}</dt>
          <dd>{L.signalList(plan.early_signals)}</dd>
        </>
      )}
      <dt>{L.primary}</dt>
      <dd>{L.signalList([plan.primary])}</dd>
      {plan.mode !== "operational" && (
        <>
          <dt>{L.guardrails}</dt>
          <dd>{L.signalList(plan.guardrails)}</dd>
        </>
      )}
      {plan.mode === "operational" && (
        <>
          <dt>{L.acceptance}</dt>
          <dd>{plan.acceptance.length ? <ul className="z-points">{plan.acceptance.map((c, i) => <li key={i}>{c}</li>)}</ul> : L.none}</dd>
        </>
      )}
      {x && (
        <>
          <dt>{L.platform}</dt>
          <dd>{x.url ? <a className="z-link" href={x.url} target="_blank" rel="noreferrer noopener">{x.platform}</a> : x.platform}</dd>
          <dt>{L.variants}</dt>
          <dd>{L.variantsText(x.variants)}</dd>
          <dt>{L.allocation}</dt>
          <dd>{L.allocations[x.allocation_unit]}</dd>
          <dt>{L.exposure}</dt>
          <dd>{x.exposure}</dd>
          <dt>{L.durationShown}</dt>
          <dd>{L.days(x.duration_days)}</dd>
          <dt>{L.metric}</dt>
          <dd>{METRIC_LABELS.option(x.metric.name, x.metric.version)}</dd>
        </>
      )}
    </dl>
  );
}

/** What a cited reference is, from the lessons and facts the person can cite; the rest are counted. */
function useCitable(productId: string) {
  const results = useQuery(q.results(productId));
  const product = useQuery(q.product(productId));
  const lessons = (results.data?.learnings ?? []).filter((l) => l.status !== "stale");
  const facts = (product.data?.facts ?? []).filter((f) => f.status === "confirmed" && typeof f.value === "string" && f.value);
  return { lessons, facts, loading: results.isLoading || product.isLoading, error: results.error ?? product.error };
}

function Expectation({ expectation: e, productId }: { expectation: C.Expectation | null; productId: string }) {
  const { lessons, facts } = useCitable(productId);
  if (!e) return <p className="z-small z-muted">{L.noExpectation}</p>;
  const named = e.evidence.flatMap((r) => {
    const lesson = r.kind === "learning" ? lessons.find((l) => l.id === r.id) : undefined;
    if (lesson) return [{ key: refKey(r), text: lessonText(lesson.statement) }];
    const fact = r.kind === "fact" ? facts.find((f) => f.id === r.id) : undefined;
    return fact ? [{ key: refKey(r), text: `${L.factRef(fact.key, r.revision)}: ${String(fact.value)}` }] : [];
  });
  const others = e.evidence.length - named.length;
  return (
    <div className="z-flow">
      <h3>{L.expectation}</h3>
      <p>{L.expectationText(e)}</p>
      <p className="z-muted">{e.rationale}</p>
      {(named.length > 0 || others > 0) && (
        <ul className="z-points z-small z-muted">
          {named.map((n) => <li key={n.key}>{n.text}</li>)}
          {others > 0 && <li>{L.citedElsewhere(others)}</li>}
        </ul>
      )}
    </div>
  );
}

function Checks<T>({ items, id, label, checked, onChange }: { items: T[]; id: (item: T) => string; label: (item: T) => string; checked: (item: T) => boolean; onChange: (item: T, on: boolean) => void }) {
  const base = useId();
  return (
    <ul className="z-check">
      {items.map((item) => {
        const key = `${base}-${id(item)}`;
        return (
          <li key={key}>
            <input id={key} type="checkbox" checked={checked(item)} onChange={(e) => onChange(item, e.target.checked)} />
            <label htmlFor={key}>{label(item)}</label>
          </li>
        );
      })}
    </ul>
  );
}

function PlanForm({ action: a, productId, onSaved }: { action: C.ActionDetail; productId: string; onSaved: (revision: number) => void }) {
  const plan = a.measurement_plan;
  const refresh = useRefresh(productId);
  const metrics = useQuery(q.metrics(productId));
  const citable = useCitable(productId);
  const x = plan.experiment;
  const e = plan.expectation;
  const [mode, setMode] = useState(plan.mode);
  const [acceptance, setAcceptance] = useState(plan.acceptance.join("\n"));
  const [design, setDesign] = useState({
    platform: x?.platform ?? "", url: x?.url ?? "", variants: x ? x.variants.map((v) => v.key).join(", ") : "control, variant",
    allocation_unit: x?.allocation_unit ?? ("user" as C.ExperimentDesign["allocation_unit"]), exposure: x?.exposure ?? "", duration_days: x?.duration_days ?? 14, metric_id: x?.metric.id ?? "",
  });
  const [direction, setDirection] = useState<"" | C.Expectation["direction"]>(e?.direction ?? "");
  const [low, setLow] = useState(e?.range?.low == null ? "" : String(e.range.low));
  const [high, setHigh] = useState(e?.range?.high == null ? "" : String(e.range.high));
  const [rationale, setRationale] = useState(e?.rationale ?? "");
  const [evidence, setEvidence] = useState<C.EvidenceRef[]>(e?.evidence ?? []);
  const [tests, setTests] = useState(plan.tests.map((t) => t.learning_id));
  const [concurrent, setConcurrent] = useState(plan.concurrent_note ?? "");
  const save = useMutation({
    mutationFn: (body: C.UpdateMeasurementPlanInput) => api.patch<C.ActionDetail>(`/api/actions/${a.id}/measurement-plan`, body),
    onSuccess: async (saved) => {
      onSaved(saved.measurement_plan.revision);
      await refresh();
    },
  });
  const definitions = metrics.data?.map((m) => m.definition) ?? [];
  const metricId = design.metric_id || definitions[0]?.id || "";
  const unit = unitIn(mode, a, mode === "experiment" ? definitions.find((d) => d.id === metricId)?.kind ?? null : null);
  const directions = (Object.keys(L.directions) as C.Expectation["direction"][]).filter((d) => unit !== "count" || d === "increase");
  const writing = Boolean(unit && direction);
  const setDesignField = <K extends keyof typeof design>(key: K, value: (typeof design)[K]) => {
    save.reset();
    setDesign({ ...design, [key]: value });
  };
  const toggleRef = (r: C.EvidenceRef, on: boolean) => setEvidence(on ? [...evidence.filter((v) => refKey(v) !== refKey(r)), r] : evidence.filter((v) => refKey(v) !== refKey(r)));
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const body: C.UpdateMeasurementPlanInput = { revision: plan.revision, tests, concurrent_note: concurrent.trim() || null };
    if (a.mode_editable && mode !== plan.mode) body.mode = mode;
    if (mode === "operational") body.acceptance = acceptance.split("\n").map((c) => c.trim()).filter(Boolean);
    if (mode === "experiment") {
      const keys = design.variants.split(",").map((k) => k.trim().toLowerCase()).filter(Boolean);
      body.experiment = {
        platform: design.platform.trim(), url: design.url.trim() || null, variants: keys.map((key, i) => ({ key, control: i === 0 })),
        allocation_unit: design.allocation_unit, exposure: design.exposure.trim(), duration_days: design.duration_days, metric_id: metricId,
      };
    }
    body.expectation = writing && direction
      ? { direction, range: low === "" && high === "" ? null : { low: low === "" ? null : Number(low), high: high === "" ? null : Number(high) }, rationale: rationale.trim(), evidence }
      : null;
    save.mutate(body);
  };
  const citedFacts = citable.facts.map((f): C.EvidenceRef => ({ kind: "fact", id: f.id, revision: f.revision }));
  return (
    <form className="z-form z-flow" onSubmit={submit}>
      {a.mode_editable && (
        <Field label={L.mode} help={L.modeHelp[mode]}>
          <Select variant="form" label={L.mode} value={mode} onValueChange={(nextValue) => { save.reset(); setMode(nextValue as Plan["mode"]); }} options={(Object.keys(L.modes) as Plan["mode"][]).map((m) => ({ value: m, label: L.modes[m] }))} />
        </Field>
      )}
      {mode === "operational" && (
        <Field label={L.acceptance} help={L.acceptanceHelp}>
          <textarea className="z-textarea" aria-label={L.acceptance} value={acceptance} onChange={(ev) => setAcceptance(ev.target.value)} required />
        </Field>
      )}
      {mode === "experiment" && (
        <div className="z-flow">
          <h3>{L.experiment}</h3>
          {!metrics.isLoading && !definitions.length ? <p className="z-small z-muted">{L.noMetricsForExperiment}</p> : (
            <>
              <Field label={L.platform}><input className="z-input" aria-label={L.platform} placeholder={L.platformPlaceholder} value={design.platform} onChange={(ev) => setDesignField("platform", ev.target.value)} minLength={2} maxLength={60} required /></Field>
              <Field label={L.url}><input className="z-input" type="url" aria-label={L.url} value={design.url} onChange={(ev) => setDesignField("url", ev.target.value)} maxLength={500} /></Field>
              <Field label={L.variants} help={L.variantsHelp}><input className="z-input" aria-label={L.variants} value={design.variants} onChange={(ev) => setDesignField("variants", ev.target.value)} required /></Field>
              <Field label={L.allocation}>
                <Select variant="form" label={L.allocation} value={design.allocation_unit} onValueChange={(nextValue) => setDesignField("allocation_unit", nextValue as C.ExperimentDesign["allocation_unit"])} options={Object.entries(L.allocations).map(([value, label]) => ({ value, label }))} />
              </Field>
              <Field label={L.exposure}><input className="z-input" aria-label={L.exposure} placeholder={L.exposurePlaceholder} value={design.exposure} onChange={(ev) => setDesignField("exposure", ev.target.value)} minLength={2} maxLength={200} required /></Field>
              <Field label={L.duration}><input className="z-input" type="number" aria-label={L.duration} value={design.duration_days} onChange={(ev) => setDesignField("duration_days", Number(ev.target.value))} min={1} max={90} step={1} required /></Field>
              <Field label={L.metric}>
                <Select variant="form" label={L.metric} value={metricId} onValueChange={(nextValue) => setDesignField("metric_id", nextValue)} options={definitions.map((d) => ({ value: d.id, label: METRIC_LABELS.option(d.name, d.version) }))} />
              </Field>
            </>
          )}
        </div>
      )}
      <div className="z-flow">
        <h3>{L.expectation}</h3>
        <p className="z-small z-muted">{L.expectationNote}</p>
        {!unit ? (
          <p className="z-small z-muted">{reasonText({ code: mode === "operational" ? "expectation_not_for_operational" : "expectation_needs_measure" })}</p>
        ) : (
          <>
            <Field label={L.direction}>
              <Select variant="form" label={L.direction} value={direction} onValueChange={(nextValue) => { save.reset(); setDirection(nextValue as typeof direction); }} options={[{ value: "", label: L.noDirection }, ...directions.map((d) => ({ value: d, label: L.directions[d] }))]} />
            </Field>
            {writing && (
              <>
                <div className="z-inline">
                  <Field label={L.rangeLabel(L.low, unit)}><input className="z-input" type="number" step="any" aria-label={L.rangeLabel(L.low, unit)} value={low} onChange={(ev) => setLow(ev.target.value)} /></Field>
                  <Field label={L.rangeLabel(L.high, unit)}><input className="z-input" type="number" step="any" aria-label={L.rangeLabel(L.high, unit)} value={high} onChange={(ev) => setHigh(ev.target.value)} /></Field>
                </div>
                <p className="z-small z-muted">{L.rangeHelp(unit)}</p>
                <Field label={L.rationale}><textarea className="z-textarea" aria-label={L.rationale} placeholder={L.rationalePlaceholder} value={rationale} onChange={(ev) => setRationale(ev.target.value)} minLength={3} maxLength={600} required /></Field>
                <Field label={L.evidence} help={L.evidenceHelp}>
                  {citable.lessons.length + citable.facts.length === 0 ? <p className="z-small z-muted">{L.noLessons}</p> : (
                    <>
                      {citable.lessons.length > 0 && <p className="z-small z-muted">{L.lessons}</p>}
                      <Checks items={citable.lessons} id={(l) => l.id} label={(l) => lessonText(l.statement)} checked={(l) => evidence.some((r) => r.kind === "learning" && r.id === l.id)} onChange={(l, on) => toggleRef({ kind: "learning", id: l.id }, on)} />
                      {citedFacts.length > 0 && <p className="z-small z-muted">{L.confirmedFacts}</p>}
                      <Checks items={citable.facts} id={(f) => f.id} label={(f) => `${L.factRef(f.key, undefined)}: ${String(f.value)}`} checked={(f) => evidence.some((r) => r.kind === "fact" && r.id === f.id)} onChange={(f, on) => toggleRef({ kind: "fact", id: f.id, revision: f.revision }, on)} />
                    </>
                  )}
                </Field>
              </>
            )}
          </>
        )}
      </div>
      <Field label={L.tests} help={L.testsHelp}>
        {citable.lessons.length ? (
          <Checks items={citable.lessons} id={(l) => l.id} label={(l) => `${lessonText(l.statement)} (${LEARNING_STATUS[l.status].label.toLowerCase()})`} checked={(l) => tests.includes(l.id)} onChange={(l, on) => setTests(on ? [...tests, l.id] : tests.filter((id) => id !== l.id))} />
        ) : <p className="z-small z-muted">{L.noLessons}</p>}
      </Field>
      <Field label={L.concurrent} help={L.concurrentHelp}>
        <textarea className="z-textarea" aria-label={L.concurrent} value={concurrent} onChange={(ev) => setConcurrent(ev.target.value)} maxLength={600} style={{ minHeight: 60 }} />
      </Field>
      <div>
        <Button tone="primary" type="submit" busy={save.isPending} disabled={mode === "experiment" && !metricId}>{L.save}</Button>
      </div>
      <ErrorText error={save.error ?? citable.error ?? metrics.error} />
    </form>
  );
}

/** The plan on the action's Overview: what will be read, how, and what the person expects. */
export function MeasurementPlanSection({ action: a, productId }: { action: C.ActionDetail; productId: string }) {
  const plan = a.measurement_plan;
  return (
    <Section title={L.section} note={L.note}>
      <p className="z-inline">
        <Pill>{L.modes[plan.mode]}</Pill>
        <span className="z-small z-muted">{L.modeBy[plan.mode_by]} · {L.revision(plan.revision)}</span>
      </p>
      <p className="z-small z-muted">{L.modeHelp[plan.mode]}</p>
      <PlanProperties plan={plan} />
      {plan.mode !== "operational" && <Expectation expectation={plan.expectation} productId={productId} />}
      {a.tested_lessons.length > 0 && (
        <div className="z-flow">
          <h3>{L.testedLessons}</h3>
          <ul className="z-points">
            {a.tested_lessons.map((l) => (
              <li key={l.id}>
                {lessonText(l.statement)} <Pill tone={LEARNING_STATUS[l.status].tone}>{LEARNING_STATUS[l.status].label}</Pill>
              </li>
            ))}
          </ul>
        </div>
      )}
      {plan.concurrent_note && <p className="z-small z-muted">{L.concurrent}: {plan.concurrent_note}</p>}
      {plan.fixed_at ? (
        <p className="z-small z-muted">{L.fixed(plan.fixed_at)}</p>
      ) : a.plan_editable ? (
        <>
          <p className="z-small z-muted">{L.editableNote}</p>
          <details className="z-disclosure">
            <summary>{L.edit}</summary>
            <PlanForm key={`${a.id}:${plan.revision}`} action={a} productId={productId} onSaved={(revision) => notify.success(L.saved(revision))} />
          </details>
        </>
      ) : null}
    </Section>
  );
}

/** An experiment's result as the platform reported it, entered by the person in the Reading block. */
export function ExperimentResultEntry({ action: a, productId, timeZone }: { action: C.ActionDetail; productId: string; timeZone: string }) {
  const plan = a.measurement_plan;
  const refresh = useRefresh(productId);
  const unit: Unit = plan.experiment?.metric.kind === "rate" ? "points" : "percent";
  const [form, setForm] = useState({ estimate: "", low: "", high: "", confidence: "95", as_of: localToday(timeZone), url: "", note: "" });
  const record = useMutation({
    mutationFn: () => api.post<C.ActionDetail>(`/api/actions/${a.id}/experiment-result`, {
      estimate: Number(form.estimate), low: Number(form.low), high: Number(form.high), confidence: Number(form.confidence), as_of: form.as_of, url: form.url.trim(), note: form.note.trim() || undefined,
    } satisfies C.RecordExperimentResultInput),
    onSuccess: async () => {
      setForm({ ...form, estimate: "", low: "", high: "", note: "" });
      await refresh();
    },
  });
  if (plan.mode !== "experiment" || !plan.experiment) return null;
  const result = a.experiment_result;
  const set = (key: keyof typeof form) => (ev: { target: { value: string } }) => {
    record.reset();
    setForm({ ...form, [key]: ev.target.value });
  };
  const live = ["live", "reading_due", "evaluated"].includes(a.status);
  return (
    <div className="z-flow">
      <h3>{L.result}</h3>
      <p className="z-small z-muted">{L.resultNote}</p>
      {result ? (
        <p>
          {L.recorded(result)}{" "}
          <a className="z-link" href={result.url} target="_blank" rel="noreferrer noopener">{L.resultUrl}</a>
          {result.note ? ` — ${result.note}` : ""}
        </p>
      ) : (
        <p className="z-small z-muted">{reasonText({ code: "experiment_result_missing" })}</p>
      )}
      {live && (
        <details className="z-disclosure" open={!result}>
          <summary>{L.recordResult}</summary>
          <form className="z-form z-flow" onSubmit={(ev) => { ev.preventDefault(); record.mutate(); }}>
            <div className="z-inline">
              <Field label={L.rangeLabel(L.estimate, unit)}><input className="z-input" type="number" step="any" aria-label={L.rangeLabel(L.estimate, unit)} value={form.estimate} onChange={set("estimate")} required /></Field>
              <Field label={L.rangeLabel(L.low, unit)}><input className="z-input" type="number" step="any" aria-label={L.rangeLabel(L.low, unit)} value={form.low} onChange={set("low")} required /></Field>
              <Field label={L.rangeLabel(L.high, unit)}><input className="z-input" type="number" step="any" aria-label={L.rangeLabel(L.high, unit)} value={form.high} onChange={set("high")} required /></Field>
            </div>
            <div className="z-inline">
              <Field label={L.confidence}><input className="z-input" type="number" aria-label={L.confidence} value={form.confidence} onChange={set("confidence")} min={80} max={99.9} step={0.1} required /></Field>
              <Field label={L.asOf}><input className="z-input" type="date" aria-label={L.asOf} value={form.as_of} onChange={set("as_of")} max={localToday(timeZone)} required /></Field>
            </div>
            <Field label={L.resultUrl}><input className="z-input" type="url" aria-label={L.resultUrl} value={form.url} onChange={set("url")} maxLength={500} required /></Field>
            <Field label={L.resultNoteField}><input className="z-input" aria-label={L.resultNoteField} value={form.note} onChange={set("note")} maxLength={400} /></Field>
            <div>
              <Button tone="primary" type="submit" busy={record.isPending}>{L.recordResult}</Button>
            </div>
            <ErrorText error={record.error} />
          </form>
        </details>
      )}
    </div>
  );
}
